import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../../api/useApi';
import { useProtocol } from '../context';
import { dateHelper } from '../../../helpers/help_functions/date_helpers';
import type { Sampling, ParameterDefinition, LaboratoryArea } from '../../../api/complementApi';

export type FilterType = 'all' | 'current' | 'last100' | 'custom';

export type DateRange = {
  from: string;
  to: string;
};

export const useLabResults = () => {
  const { protocol } = useProtocol();
  const api = useApi();

  const [samplings, setSamplings] = useState<Sampling[]>([]);
  const [selectedSampling, setSelectedSampling] = useState<Sampling | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [dateRange, setDateRange] = useState<DateRange>({ from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [parameterDefinitions, setParameterDefinitions] = useState<ParameterDefinition[]>([]);
  const [laboratoryAreas, setLaboratoryAreas] = useState<LaboratoryArea[]>([]);

  const fetchSamplings = useCallback(async () => {
    if (!protocol?.deceased) return;

    setLoading(true);
    try {
      const params: Record<string, unknown> = { deceased: protocol.deceased };

      if (filterType === 'current') {
        const today = dateHelper.todayISOLocal();
        params.made_at__gte = today;
        params.made_at__lte = today;
      } else if (filterType === 'last100') {
        params.limit = 100;
      } else if (filterType === 'custom') {
        if (dateRange.from) params.made_at__gte = `${dateRange.from}T00:00:00`;
        if (dateRange.to) params.made_at__lte = `${dateRange.to}T23:59:59`;
      }

      const res = await api.complement.samplings.get(params as any);
      setSamplings(res.data.results);
      setSelectedSampling(null);
    } catch (err) {
      setSamplings([]);
    } finally {
      setLoading(false);
    }
  }, [protocol?.deceased, filterType, dateRange]);

  useEffect(() => {
    fetchSamplings();
  }, [fetchSamplings]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [paramRes, areaRes] = await Promise.all([
          api.complement.parameterDefinitions.get({ limit: 1000 } as any),
          api.complement.laboratoryAreas.get({ limit: 1000 }),
        ]);
        setParameterDefinitions(paramRes.data.results);
        setLaboratoryAreas(areaRes.data.results);
      } catch {
        // fallback to empty — trends will still group by name
      }
    };
    loadReferenceData();
  }, []);

  const handleSamplingSelect = (sampling: Sampling) => {
    setSelectedSampling(sampling);
  };

  const getResultValue = (result: Sampling['parameter_results'][number]): string => {
    switch (result.result_type) {
      case 'QUANTITATIVE':
        return result.value || '';
      case 'QUALITATIVE':
        return result.text_value || '';
      case 'TEXT':
        return result.rich_text_value?.replace(/<[^>]*>/g, '') || '';
      case 'COMMENT':
        return result.comment_value || '';
      default:
        return result.value || result.text_value || '';
    }
  };

  const copyToClipboard = async (data: Sampling | Sampling['parameter_results']) => {
    let text = '';

    if (Array.isArray(data)) {
      text = data
        .map((item) => {
          const value = getResultValue(item);
          return `${item.parameter_name}: ${value} ${item.parameter_units || ''}`;
        })
        .join('\n');
    } else {
      const time = new Date(data.made_at).toLocaleString('cs-CZ');
      text = `Laboratorní výsledky - ${time}\nID vzorku: ${data.external_id ?? '–'}\n\n`;
      data.parameter_results.forEach((result) => {
        const value = getResultValue(result);
        const oor = result.is_out_of_range === 'true' ? ' (mimo normu)' : '';
        text += `${result.parameter_name}: ${value} ${result.parameter_units || ''}${oor}\n`;
      });
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-999999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch {
      alert('Kopírování selhalo.\n\n' + text);
    }
  };

  const applyCustomDateRange = (range: DateRange) => {
    setDateRange(range);
  };

  return {
    samplings,
    selectedSampling,
    filterType,
    dateRange,
    loading,
    parameterDefinitions,
    laboratoryAreas,
    setFilterType,
    applyCustomDateRange,
    handleSamplingSelect,
    copyToClipboard,
    getResultValue,
  };
};
